import React, { useEffect, useId, useRef, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Check, Loader2, X } from 'lucide-react';

// A preview never reserves or redeems a code. Checkout verifies it again.
export default function PromotionCodeField({ value, onChange, onStatusChange, teamId = null, organizationId = null, disabled = false, sv = true, initialQuote = null }) {
  const id = useId();
  const [quote, setQuote] = useState(initialQuote);
  const initialContext = useRef({ value, teamId, organizationId });
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);
  const generation = useRef(0);
  const mounted = useRef(true);
  const callback = useRef(onStatusChange);
  callback.current = onStatusChange;
  const tr = (a, b) => sv ? a : b;
  const money = amount => new Intl.NumberFormat(sv ? 'sv-SE' : 'en-GB', { style: 'currency', currency: 'SEK' }).format(amount / 100);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; generation.current++; };
  }, []);
  useEffect(() => {
    generation.current++;
    if (initialContext.current?.value === value && initialContext.current?.teamId === teamId && initialContext.current?.organizationId === organizationId && initialQuote?.code?.toUpperCase() === value.trim().toUpperCase()) {
      callback.current?.({ ready: true, busy: false, quote: initialQuote });
      return;
    }
    initialContext.current = null;
    setQuote(null); setError(''); setChecking(false);
    callback.current?.({ ready: !value.trim(), busy: false, quote: null });
  }, [value, teamId, organizationId]);
  function edit(next) {
    initialContext.current = null;
    generation.current++;
    setQuote(null); setError(''); setChecking(false);
    callback.current?.({ ready: !next.trim(), busy: false, quote: null });
    onChange(next);
  }
  async function apply() {
    if (checking || disabled || !value.trim()) return;
    const current = ++generation.current;
    setChecking(true); setError(''); setQuote(null);
    callback.current?.({ ready: false, busy: true, quote: null });
    try {
      const { data } = await api.post('/billing/promotion-codes/preview', {
        code: value.trim(), team_id: teamId, organization_id: teamId ? null : organizationId,
      });
      if (!mounted.current || current !== generation.current) return;
      setQuote(data);
      callback.current?.({ ready: true, busy: false, quote: data });
    } catch (e) {
      if (!mounted.current || current !== generation.current) return;
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : tr('Koden kunde inte kontrolleras. Försök igen.', 'Could not check the code. Please retry.'));
      callback.current?.({ ready: false, busy: false, quote: null });
    } finally {
      if (mounted.current && current === generation.current) setChecking(false);
    }
  }
  const discount = quote?.discount;
  const duration = discount?.duration === 'forever' ? tr('Tills vidare', 'Ongoing')
    : discount?.duration === 'once' ? tr('Engångsrabatt', 'One-time discount')
    : `${discount?.months} ${tr('månader från aktivering', 'months from activation')}`;
  return <div className="space-y-2">
    <Label htmlFor={id}>{tr('Rabattkod (valfritt)', 'Promotion code (optional)')}</Label>
    <div className="flex items-center gap-2">
      <Input id={id} value={value} maxLength={40} autoComplete="off" autoCapitalize="characters" spellCheck={false}
        className="min-w-0 flex-1" placeholder="CITY26" disabled={disabled} aria-invalid={Boolean(error)} aria-describedby={`${id}-feedback`}
        onChange={e => edit(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); apply(); } }} />
      <Button type="button" variant="outline" className="shrink-0" disabled={disabled || checking || !value.trim() || Boolean(quote)} onClick={apply}>
        {checking ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : quote ? <Check className="mr-1.5 h-4 w-4" /> : null}
        {quote ? tr('Tillämpad', 'Applied') : tr('Använd koden', 'Apply code')}
      </Button>
    </div>
    <div id={`${id}-feedback`} aria-live="polite">
      {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
      {quote ? <div className="space-y-2 rounded-lg border border-primary/25 bg-primary/5 p-3 text-sm">
        <div className="flex items-start justify-between gap-2"><div><p className="font-medium">{quote.code} · {discount.kind === 'percent' ? `${discount.value} %` : money(discount.value)} {tr('rabatt', 'off')}</p><p className="text-xs text-muted-foreground">{duration}</p></div>
          <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs" disabled={disabled} onClick={() => edit('')}><X className="mr-1 h-3 w-3" />{tr('Ta bort', 'Remove')}</Button></div>
        <p className="text-xs">{tr('Beräknat pris när rabatten gäller:', 'Estimated price while the discount applies:')} <strong>{money(quote.discounted_amount_ore)}</strong> <span className="text-muted-foreground">({tr('ordinarie', 'regular')} {money(quote.monthly_amount_ore)}/{tr('mån', 'mo')})</span></p>
        {quote.warnings?.map((warning, index) => <p key={index} className="text-xs leading-relaxed text-muted-foreground">{warning}</p>)}
        <p className="text-xs text-muted-foreground">{tr('Villkor och slutbelopp bekräftas i Stripe. Koden är inte förbrukad av denna kontroll.', 'Terms and final amount are confirmed in Stripe. This check does not redeem the code.')}</p>
      </div> : !error && <p className="text-xs text-muted-foreground">{tr('Har du en kod? Kontrollera den före aktiveringen.', 'Have a code? Check it before activation.')}</p>}
      {value.trim() && !quote && !checking && <button type="button" disabled={disabled} onClick={() => edit('')} className="mt-1 text-xs underline underline-offset-2">{tr('Ta bort koden och fortsätt utan rabatt', 'Remove code and continue without a discount')}</button>}
    </div>
  </div>;
}
